import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  List,
  ListItem,
  Pagination,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material'
import { useIsOpenMenuLeftContext } from '../../common/utils/isOpenMenuLeftContext'
import { NotificationItem } from '../../components/notification-item'
import { useNotificationsContext } from '../../common/utils/NotificationsContext'
import { SendingStatus } from '../../modules/notifications/enums/SendingStatus'
import { EventType } from '../../modules/notifications/enums/EventType'
import i18n from 'i18next'
import styles from './Notifications.module.css'

const getEventTypeGroups = () => ({
  all: {
    label: i18n.t('allEvents'),
    types: Object.values(EventType).filter((v) => typeof v === 'number') as EventType[],
  },
  calendar: {
    label: i18n.t('calendar'),
    types: [
      EventType.AppointmentCreated,
      EventType.AppointmentUpdated,
      EventType.AppointmentCancelled,
      EventType.CalendarEventCreated,
      EventType.CalendarEventReminder,
    ],
  },
  training: {
    label: i18n.t('training'),
    types: [EventType.TrainingAssigned, EventType.TrainingCompleted],
  },
  forms: {
    label: i18n.t('forms'),
    types: [EventType.FormSubmitted, EventType.FormCompleted],
  },
  resources: {
    label: i18n.t('resources'),
    types: [EventType.ResourceShared, EventType.ResourceUpdated],
  },
  articles: {
    label: i18n.t('articles'),
    types: [EventType.ArticlePublished, EventType.ArticleUpdated],
  },
})

export const Notifications = () => {
  const { t } = useTranslation()
  const { openMenuLeft } = useIsOpenMenuLeftContext()
  const [currentPage, setCurrentPage] = useState(1)
  const [showUnreadOnly, setShowUnreadOnly] = useState(false)
  const [categoryFilter, setCategoryFilter] = useState<string>('all')

  const itemsPerPage = 10
  const eventTypeGroups = getEventTypeGroups()
  const { notifications, unreadCount, markAsRead } = useNotificationsContext()

  const filteredNotifications = notifications.filter((notification) => {
    const passesUnreadFilter = showUnreadOnly
      ? notification.deliveryStatus === SendingStatus.Pending
      : true

    const passesCategoryFilter =
      categoryFilter === 'all'
        ? true
        : eventTypeGroups[categoryFilter as keyof typeof eventTypeGroups]?.types?.includes(
            notification.eventType
          )

    return passesUnreadFilter && passesCategoryFilter
  })

  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage)
  const paginatedNotifications = filteredNotifications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  )

  const onNotificationClick = async (notification: any) => {
    if (notification.deliveryStatus === SendingStatus.Pending) {
      await markAsRead(notification.id)
    }
  }

  const handleCategoryChange = (event: SelectChangeEvent) => {
    setCategoryFilter(event.target.value as string)
    setCurrentPage(1)
  }

  return (
    <div
      className={styles.notificationsContainer}
      style={{ marginLeft: openMenuLeft ? '50px' : '' }}
    >
      <div
        className={styles.notificationsContent}
        style={{ maxWidth: openMenuLeft ? 'calc(100% - 250px)' : 'calc(100% - 300px)' }}
      >
        <Box className={styles.notificationsBox}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <FormControl sx={{ minWidth: 200 }}>
              <InputLabel
                id="category-filter-label"
                sx={{
                  color: 'var(--color-texto)',
                  '&.Mui-focused': {
                    color: 'var(--verde-topbar-y-botones)',
                  },
                }}
              >
                {t('filterByCategoryEvent')}
              </InputLabel>
              <Select
                labelId="category-filter-label"
                value={categoryFilter}
                label={t('filterByCategoryEvent')}
                onChange={handleCategoryChange}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--verde-topbar-y-botones)',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--verde-topbar-y-botones)',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'var(--verde-topbar-y-botones)',
                  },
                }}
              >
                {Object.entries(eventTypeGroups).map(([key, group]) => (
                  <MenuItem key={key} value={key}>
                    {group.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControlLabel
              control={
                <Switch
                  checked={showUnreadOnly}
                  onChange={(e) => {
                    setShowUnreadOnly(e.target.checked)
                    setCurrentPage(1)
                  }}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: 'var(--verde-topbar-y-botones)',
                      '&:hover': {
                        backgroundColor: 'rgba(var(--verde-topbar-y-botones-rgb), 0.08)',
                      },
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: 'var(--verde-topbar-y-botones)',
                    },
                  }}
                />
              }
              label={t('showUnreadOnly')}
            />
          </Box>

          {filteredNotifications.length === 0 ? (
            <Typography variant="body1" className={styles.noNotificationsText}>
              {showUnreadOnly ? t('noUnreadNotifications') : t('noNotifications')}
            </Typography>
          ) : (
            <>
              <List className={styles.notificationsList}>
                {paginatedNotifications.map((notification) => (
                  <ListItem
                    key={notification.id}
                    divider
                    onClick={() => onNotificationClick(notification)}
                    className={styles.notificationListItem}
                    sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)',
                        cursor: 'pointer',
                      },
                    }}
                  >
                    <NotificationItem
                      title={notification.title || t('newNotification')}
                      message={notification.message || ''}
                      date={notification.createdAt}
                      eventType={notification.eventType}
                      deliveryStatus={notification.deliveryStatus}
                      size="normal"
                    />
                  </ListItem>
                ))}
              </List>

              {totalPages > 1 && (
                <Box className={styles.paginationContainer}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={(_, page) => setCurrentPage(page)}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: 'var(--color-texto)',
                      },
                      '& .MuiPaginationItem-root.Mui-selected': {
                        backgroundColor: 'var(--verde-topbar-y-botones)',
                        color: 'white',
                      },
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </div>
    </div>
  )
}
